import { z } from "zod";

export const ArtifactTypeSchema = z.enum(["xlsx", "md", "docx", "pptx", "pdf", "txt", "zip", "html"]);

export type ArtifactType = z.infer<typeof ArtifactTypeSchema>;

export type ArtifactScope = "workspace" | "conversation";

export interface ConversationArtifact {
  id: string;
  conversationId: string;
  propelAuthUserID?: string;
  type: ArtifactType;
  name: string;
  description: string | null;
  path: string;
  url: string;
  scope: ArtifactScope;
  createdAt: string;
  updatedAt: string;
}

const ConversationArtifactObjectCellSchema = z
  .object({
    value: z.unknown(),
    source: z.string().optional(),
    documentId: z.string().optional(),
    href: z.string().optional(),
  })
  .passthrough();

export const ConversationArtifactCellSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  ConversationArtifactObjectCellSchema,
]);

export type ConversationArtifactCell = z.infer<typeof ConversationArtifactCellSchema>;

export const ConversationArtifactRowSchema = z.object({
  cells: z.array(ConversationArtifactCellSchema),
});

export type ConversationArtifactRow = z.infer<typeof ConversationArtifactRowSchema>;

export const ArtifactPreviewBodySchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("xlsx"),
    columns: z.array(z.string()).optional(),
    rows: z.array(ConversationArtifactRowSchema).optional(),
  }),
  z.object({ type: z.literal("md"), content: z.string() }),
  z.object({ type: z.literal("txt"), content: z.string() }),
  z.object({ type: z.literal("docx") }),
  z.object({ type: z.literal("pptx") }),
  z.object({ type: z.literal("pdf") }),
  z.object({ type: z.literal("zip") }),
  z.object({ type: z.literal("html"), content: z.string() }),
]);

export const AttachmentPreviewResponseSchema = z.object({
  preview: ArtifactPreviewBodySchema.nullable(),
});

export function attachmentPreviewPath(attachmentId: string): string {
  return `/api/conversation-attachments-files/${encodeURIComponent(attachmentId)}/preview`;
}

export function isObjectCell(
  cell: ConversationArtifactCell,
): cell is Exclude<ConversationArtifactCell, string | number | boolean | null> {
  return typeof cell === "object" && cell !== null;
}

export function cellDisplayText(cell: ConversationArtifactCell): string {
  if (cell === null) return "";
  if (isObjectCell(cell)) return String(cell.value ?? "");
  return String(cell);
}
